import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { FileSizePipe, SharedModule } from '@aiwyn/shared';
import { CrlItemFile, CrlRequestItem } from '../../../../models';
import { CrlDocumentsTabMapper, CrlFileDateGroup, CrlStagingRow } from './crl-documents-tab.mapper';
import { getCrlItemAlert } from '../../../../shared/utils/crl-item-alert.util';
// Reused leaf icon component from the legacy request panel: derives the colored
// filetype icon from the filename (getFileIcon) and renders flag-pennant for
// flagged files — the same icons the Figma rows use (via <awn-file-item>).
import { FileItemIconComponent } from '../../../../../client-requests/containers/client-request-details/client-request-panel/components/file-item-icon/file-item-icon.component';

/** How many client-uploaded files to show before "View All Files" reveals the rest. */
const COLLAPSED_FILE_LIMIT = 20;

/** A client-file date group with the in-progress upload rows shown at its foot (Today only). */
interface CrlClientFileGroup extends CrlFileDateGroup {
  staging: CrlStagingRow[];
  /** Every file the day holds, including any the collapsed view is not rendering. */
  totalCount: number;
}

/**
 * Documents tab of the item-detail overlay. Top to bottom (Figma 12421-8373):
 *   1. A firm flag banner (when the item is `NEEDS_UPDATE` with a `flagMessage`).
 *   2. "From your firm" reference files (single header + count, download-only).
 *   3. Client-uploaded files grouped by upload day, newest first — with any
 *      in-progress DMS uploads rendered at the foot of the "Today" group, flagged
 *      files rendered inline (red, "Replace File"), and a "View All Files" toggle
 *      once the client list exceeds the collapsed limit.
 *
 * Presentational: per-file actions bubble as outputs so the container owns the
 * domain calls (cxp-architecture: sub-components never inject domain/API
 * services). Staging rows are computed by the container from DMS state and
 * passed in via `stagingRows`.
 */
@Component({
  selector: 'cxp-crl-documents-tab',
  templateUrl: './crl-documents-tab.component.html',
  styleUrls: ['./crl-documents-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, FileSizePipe, FileItemIconComponent],
})
export class CrlDocumentsTabComponent {
  readonly item = input.required<CrlRequestItem>();
  /** In-progress / just-finished DMS uploads for this item (from the container). */
  readonly stagingRows = input<CrlStagingRow[]>([]);
  /**
   * Whether the item is in an editable state (open/needs-update — before "I'm
   * Finished" and after Undo). Gates the destructive per-file "X" remove action:
   * once the item is finished / in review / N/A, files can't be deleted. Fed from
   * the container's `canMarkFinished` — the same "editable" signal the sibling
   * questionnaire's `canEdit` uses.
   */
  readonly canEdit = input<boolean>(false);

  /** Bubbled to the container (file actions). */
  readonly previewRequested = output<CrlItemFile>();
  readonly downloadRequested = output<CrlItemFile>();
  readonly replaceFileRequested = output<CrlItemFile>();
  readonly removeFileRequested = output<CrlItemFile>();
  readonly cancelUploadRequested = output<CrlStagingRow>();

  /**
   * Whether this item has anything uploading. Kept as a boolean because
   * `stagingRows` is rebuilt on every progress event — and on unrelated uploads
   * elsewhere in the app — so a consumer reading it directly recomputes on that
   * churn. This settles to a value and wakes them only on the transition.
   */
  private readonly hasStagingRows = computed(() => this.stagingRows().length > 0);

  /**
   * Whether the client list is showing in full rather than collapsed to
   * {@link COLLAPSED_FILE_LIMIT}. Set by "View All Files", and by uploading.
   */
  protected readonly showAll = signal(false);

  constructor() {
    // An upload opens the list and leaves it open. The collapsed view keeps the
    // FIRST files while an upload lands at the end, so on an item already near
    // the limit the client would watch a staging row fill up and then be replaced
    // by nothing they can see. Staying open also carries them across that swap,
    // which unlatching when the upload finished would cut short.
    //
    // A latch is state, not derivation, so it cannot be a `computed`. Setting a
    // signal from an effect is safe here: nothing downstream of `showAll` feeds
    // back into `hasStagingRows`, so there is no cycle.
    effect(() => {
      if (this.hasStagingRows()) this.showAll.set(true);
    });
  }

  private readonly files = computed<CrlItemFile[]>(() => this.item().files ?? []);

  /** Firm-provided reference files ("From your firm"). */
  protected readonly referenceFiles = computed<CrlItemFile[]>(() => this.files().filter((file) => file.isReference));
  protected readonly referenceCount = computed(() => this.referenceFiles().length);
  protected readonly hasReferenceFiles = computed(() => this.referenceCount() > 0);

  /** Client-uploaded files (everything not firm-provided). */
  private readonly clientFiles = computed<CrlItemFile[]>(() => this.files().filter((file) => !file.isReference));
  protected readonly clientFileCount = computed(() => this.clientFiles().length);
  protected readonly hasMore = computed(() => this.clientFileCount() > COLLAPSED_FILE_LIMIT);

  /**
   * Condition-driven alert (overdue / changes-requested / both) shown once at the
   * top of the tab, or null when the item needs no alert. Headline + copy are
   * system text; the firm's own note renders italic below (changes cases only).
   */
  protected readonly alert = computed(() => getCrlItemAlert(this.item()));

  /**
   * Client files grouped by upload day (capped when collapsed), with the staging
   * rows attached to the "Today" group — synthesizing a leading "Today" group
   * when uploads are in flight but no client file was uploaded today yet.
   *
   * The template renders those rows AFTER the group's real files — the seat a
   * file lands in once the server has it. An upload therefore never moves: it
   * fills with progress there, takes the done check there, and is swapped for
   * the real row there.
   */
  protected readonly clientGroups = computed<CrlClientFileGroup[]>(() => {
    const allFiles = this.clientFiles();
    const visible = this.showAll() ? allFiles : allFiles.slice(0, COLLAPSED_FILE_LIMIT);

    // Counts from the whole list, rows from the capped one, so a day that is only
    // partly rendered still reports everything it holds.
    const dayTotals = new Map(CrlDocumentsTabMapper.toDateGroups(allFiles).map((group) => [group.key, group.files.length]));
    const groups = CrlDocumentsTabMapper.toDateGroups(visible).map((group) => ({
      ...group,
      totalCount: dayTotals.get(group.key) ?? group.files.length,
    }));

    const staging = this.stagingRows();
    if (staging.length === 0) {
      return groups.map((group) => ({ ...group, staging: [] }));
    }

    const hasToday = groups.length > 0 && groups[0].label === 'Today';
    if (hasToday) {
      return groups.map((group, index) => ({ ...group, staging: index === 0 ? staging : [] }));
    }
    return [
      { key: 'today-staging', label: 'Today', files: [], totalCount: 0, staging },
      ...groups.map((group) => ({ ...group, staging: [] })),
    ];
  });

  /** Nothing at all to show — no reference files, no client files, no uploads. */
  protected readonly isEmpty = computed(
    () => !this.hasReferenceFiles() && this.clientFileCount() === 0 && !this.hasStagingRows(),
  );

  onViewAll(): void {
    this.showAll.set(true);
  }
}
